import { useMemo, useSyncExternalStore } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { chatStore } from "./chat-store";

export type ChatRequest = { newChat: true; prompt?: string } | { thread: string } | { history: true };
export interface ChatThreadSummary {
  id: string;
  title: string;
  updatedAt: string;
}
// Home's empty cover is descriptive; Chat's actual starters come from the shared API.
export const STARTER_PROMPTS = ["Find something you saved", "Explore your ideas"];
const NONE: ChatThreadSummary[] = [];
const summaries = new WeakMap<ReturnType<typeof chatStore>, { key: string; value: ChatThreadSummary[] }>();
const noSubscribe = () => () => undefined;
export function useChatThreads(userId: string | undefined, live = true): ChatThreadSummary[] {
  const client = useQueryClient();
  const store = useMemo(() => (userId ? chatStore(userId, client) : undefined), [userId, client]);
  useChatSync(store, live);
  const snapshot = useMemo(
    () => () => {
      if (!store) return NONE;
      const value = store
        .snapshot()
        .conversations.map(({ id, title, updatedAt }) => ({ id, title, updatedAt: new Date(updatedAt).toISOString() }));
      const key = JSON.stringify(value),
        previous = summaries.get(store);
      if (previous?.key === key) return previous.value;
      summaries.set(store, { key, value });
      return value;
    },
    [store],
  );
  return useSyncExternalStore(live && store ? store.subscribe : noSubscribe, snapshot, () => NONE);
}

export function useChatSync(store: ReturnType<typeof chatStore> | undefined, active = true) {
  useQuery({
    queryKey: ["chat-history", store?.accountId],
    queryFn: () => store!.sync(),
    enabled: active && !!store,
    staleTime: 20_000,
    refetchInterval: 20_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: false,
  });
}

// A request is handed to Chat in memory, never through the address or navigation
// state: Chat is a retained tab that only follows the address, which stays /chat.
// The asker calls requestChat, then opens /chat; Chat applies the request once and
// takes it.
let pending: { id: number; request: ChatRequest } | null = null;
let lastId = 0;
const requestListeners = new Set<() => void>();

const announce = () => requestListeners.forEach(listener => listener());

export const requestChat = (request: ChatRequest) => {
  lastId += 1;
  pending = { id: lastId, request };
  announce();
};

export const takeChatRequest = (id: number) => {
  if (pending?.id !== id) return;
  pending = null;
  announce();
};

/** Deliver a retained-page request, including one queued before Chat mounts. */
export function subscribeChatRequests(listener: (value: NonNullable<typeof pending>) => void) {
  const changed = () => {
    if (pending) listener(pending);
  };
  requestListeners.add(changed);
  changed();
  return () => {
    requestListeners.delete(changed);
  };
}
